import { ref, computed } from 'vue'
import Fuse, { type IFuseOptions } from 'fuse.js'
import { fetchSupplierNames } from '../services/supplierApi'

interface SupplierName {
    id: string
    name: string
}

const supplierNames = ref<SupplierName[]>([])
const loaded = ref(false)

// Shared Fuse instance — rebuilt when names are loaded
let fuseInstance: Fuse<SupplierName> | null = null

const FUSE_OPTIONS: IFuseOptions<SupplierName> = {
    keys: ['name'],
    threshold: 0.4,       // 0 = exact, 1 = match anything
    distance: 200,        // how far from the expected position a match can be
    includeScore: true,
    minMatchCharLength: 2,
}

/**
 * Load all supplier names once (shared across components).
 */
export async function loadSupplierNames(token: string) {
    if (loaded.value) return
    try {
        supplierNames.value = await fetchSupplierNames(token)
        fuseInstance = new Fuse(supplierNames.value, FUSE_OPTIONS)
        loaded.value = true
    } catch (e) {
        console.error('Failed to load supplier names for fuzzy search:', e)
    }
}

/**
 * Fuzzy search supplier names. Returns matched IDs ordered by relevance.
 */
export function fuzzySearchSuppliers(query: string): { ids: string[], suggestions: string[] } {
    if (!fuseInstance || !query || query.length < 2) {
        return { ids: [], suggestions: [] }
    }

    const results = fuseInstance.search(query)
    return {
        ids: results.map(r => r.item.id),
        suggestions: results.slice(0, 5).map(r => r.item.name)
    }
}

export function useFuzzySupplierSearch() {
    return {
        supplierNames: computed(() => supplierNames.value),
        loaded: computed(() => loaded.value),
        loadSupplierNames,
        fuzzySearchSuppliers,
    }
}
